/**
 * Where the `.fig` import service lives, for THIS build, at THIS origin.
 *
 * The cloud build bakes an absolute URL because it knows its own origin at
 * build time. An appliance does not: the same bytes are reached at
 * `https://10.30.1.244:8443`, at `https://studio-dgx-dev.cogo.xyz`, and
 * through a local proxy at `http://127.0.0.1:8899`. Baking any one of those
 * would break the other two, so an appliance configures a PATH and this
 * resolves it against wherever the app is actually running.
 *
 * It has to be resolved HERE rather than left relative because the client does
 * `new URL(uploadUrl, base + "/")`, which throws on a relative base — so a
 * path would fail at the first upload instead of at configuration time, which
 * is the worst place to find out.
 */
export function resolveOpenPencilApiBase(
  configured: string | undefined,
  origin: string,
): string | null {
  const value = (configured ?? "").trim();
  // Empty is the DISCLOSURE, not a default: Studio reports .fig intake
  // unavailable rather than dialling somewhere. Resolving "" against an origin
  // would quietly turn that into "import from the app's own root".
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value.replace(/\/$/, "");
  // A path with nothing to resolve it against is not a base, and inventing an
  // origin would send requests to a host nobody configured.
  if (!origin) return null;
  try {
    return new URL(value, origin).toString().replace(/\/$/, "");
  } catch {
    return null;
  }
}
